/* screens/workspace/feed/DraftEntityCard.jsx — редактируемый inline-черновик сущности (S6).
   Типы: card (Сырьё) / ttk (ТТК) / menu (Меню). Жизненный цикл:
   editing → committing → committed (collapse «Добавлено →») | dismissed (collapse) | error.
   Пересчёт derived при правке чисел — debounce 600мс через боевой recomputeDraft (POST
   /api/chats/draft/recompute/, Decimal→Number), БЕЗ автоскролла ленты (фикс №29: меняется
   только локальный стейт карточки). Коммит → POST /api/chats/draft/commit/ (через провайдер
   commitDraft + replaceDraft, идемпотентно по message_id-маркеру). Живое состояние черновика
   публикуется наверх (registerDraftLive) → уходит в draft_context следующего сообщения, чтобы
   модель правила черновик с учётом ручных правок. Спец-случай for_menu: без кнопки «Утвердить»,
   метка «Привязан к меню» (привяжется при коммите меню). Неподдержанный тип → fallback ВСЕГДА с
   кнопкой «Закрыть» (фикс №22 trapped state). «Уточнить» → prefill композера. Единый AI-визуал с
   ConfirmActionCard. Источник: 07 §2.14, 05 сценарий 1. ХЕНДОФФ → frontend/components/chat/DraftEntityCard.jsx. */

import React from 'react';
import { useAuth } from '../../../app/providers/AuthProvider.jsx';
import { useChat } from '../../../app/providers/ChatProvider.jsx';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { AllergenAutoHint } from '../../../components/kitchen/AllergenControls.jsx';
import { KitchenPicker, MultiGramsStep } from '../../../components/kitchen/IngredientPickers.jsx';
import { KbjuTable } from '../../../components/ttk/TTKViewBlocks.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { NativeSelect, NumberInput, Segmented, TextInput, Textarea } from '../../../components/ui/Field.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { CATEGORY_SLUG_TO_LABEL, EU_ALLERGENS } from '../../../lib/adapters/ingredients.js';
import { subscriptionReal } from '../../../lib/adapters/subscription.js';
import { pickerFetch } from '../../../lib/api/index.js';
import { SUBSCRIPTION_EXPIRED, tariffTarget, treeSkippedNote, treeStopNote } from '../../../lib/aiAccess.js';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';

  const { useState, useRef, useEffect, useCallback } = React;
  const { useNavigate } = RR;

  // 'm123' → 123 (числовой server message_id для идемпотентного commit). Локальный 'a…' → null.
  const numericMsgId = (mid) => {
    const m = /^m(\d+)$/.exec(mid || '');
    return m ? Number(m[1]) : null;
  };

  // Глобальная UX-настройка «не предупреждать о списании запросов при создании
  // меню» (чекбокс «Больше не спрашивать»). Хранится в localStorage.
  const MENU_QUOTA_SKIP_KEY = 'ui:skip-menu-quota-modal';

  // Русское склонение «блюдо/блюда/блюд» по числу.
  const plBlyud = (n) => {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return 'блюд';
    if (b > 1 && b < 5) return 'блюда';
    if (b === 1) return 'блюдо';
    return 'блюд';
  };

  const KIND_LABEL = { card: 'Сырьё', ttk: 'ТТК', menu: 'Меню' };
  const KIND_ICON = { card: 'tag', ttk: 'book', menu: 'reports' };
  // Канон 17 категорий = ttk.constants.IngredientCategory (slug → RU); payload.category — slug,
  // как присылает LLM/бэк. Раньше тут жили 6 латинских значений с «vegetable» (singular) —
  // не совпадали со slug'ами бэка, и NativeSelect фолбэчил на первую опцию (огурец → «Молочное»).
  const CATEGORIES = Object.entries(CATEGORY_SLUG_TO_LABEL).map(([value, label]) => ({ value, label }));
  // Канон 14 групп EU (= ttk.constants.EU_ALLERGENS, он же ТР ТС 022/2011) — ровно те ярлыки,
  // что хранит/ждёт бэк, включая «Молоко (лактоза)». Раньше тут был усечённый список из 8 с
  // «Молоко»: 6 групп отсутствовали (Арахис/Горчица/Кунжут/Сульфиты/Люпин/Моллюски), а молоко
  // не подсвечивалось как выбранное (бэк/LLM присылают «Молоко (лактоза)»).
  const ALLERGENS = EU_ALLERGENS;

  /* ── вложенный состав п/ф (F40 W7 §20.6): листовые строки sub_create.ingredient_rows ──
     Читается ЛОКАЛЬНО из payload (бэк-запрос не нужен — состав уже в payload бандла).
     Глубокий иммутабельный патч делает родительская карточка (setSubRow/setSubField) —
     shallow-spread вложенного состава терял бы правку молча.
     Поле «Выход» показываем ТОЛЬКО для плоского наброска (без состава): при
     наличии состава выход п/ф ВЫВОДИТ КОД из суммы выходов компонентов
     («значения под блюдо») — поле «партии» только путало бы. */
  function SubRows({ rowIndex, sub, usageNettoG, onSubField, onSubFieldText, onSubRow, onSubRowRemove, onSubRowAdd }) {
    const rows = sub.ingredient_rows || [];
    // Переключатель вида: «Для блюда» (дефолт) — read-only превью, сколько
    // каждого компонента уходит в это блюдо (factor = нетто строки-ПФ ÷ выход).
    // «На кг» — РЕДАКТИРУЕМАЯ раскладка на 1 кг (бэкенд канонизирует состав ПФ к
    // выходу 1000 г — ПФ заготавливают крупной партией; правишь именно тут).
    const sumOut = rows.reduce((a, sr) => a + (Number(sr.weight_in_dish_g) || 0), 0);
    const outG = Number(sub.output_g) || sumOut;
    const useG = usageNettoG == null ? null : Number(usageNettoG);
    const canDish = outG > 0 && useG != null && Number.isFinite(useG) && useG > 0;
    const [mode, setMode] = useState('dish');
    const eff = (mode === 'dish' && canDish) ? 'dish' : 'kg';
    const factor = eff === 'dish' ? useG / outG : 1;
    const dishG = (v) => (v == null || v === '' ? null : Math.round(Number(v) * factor * 10) / 10);
    return (
      <div className="draft-subrows">
        <div className="draft-subrows__meta">
          <span className="draft-subrows__kicker">Состав полуфабриката</span>
          {canDish && (
            <Segmented options={[{ value: 'dish', label: 'Для блюда' }, { value: 'kg', label: 'На кг' }]}
              value={eff} onChange={setMode} size="sm" ariaLabel="Масштаб состава полуфабриката" />
          )}
          {eff === 'dish'
            ? <span className="text-mute">в блюде: {useG} г</span>
            : (rows.length === 0 && (
                <label className="draft-subrows__out"><span className="field__label">Выход п/ф</span>
                  <NumberInput plain value={sub.output_g ?? ''} unit="г" step={10} min={0}
                    onChange={(v) => onSubField(rowIndex, { output_g: v === '' ? null : Number(v) })} /></label>
              ))}
        </div>
        {rows.length === 0
          ? <p className="draft-subrows__empty text-mute">Состав пока не собран — рецепт п/ф достроится автоматически после утверждения.</p>
          : eff === 'dish'
            ? (
              <div className="draft-rows draft-rows--sub">
                <div className="draft-rows__head">
                  <span>Компонент</span><span className="num">Брутто</span><span className="num">Нетто</span><span className="num">Выход</span><span />
                </div>
                {rows.map((sr, j) => {
                  const b = dishG(sr.amount_brutto_g), n = dishG(sr.amount_netto_g), w = dishG(sr.weight_in_dish_g);
                  return (
                    <div className="draft-row draft-row--readonly" key={sr.key || j}>
                      <span className="draft-row__cell draft-row__cell--name" data-label="Компонент"><span className="truncate" title={sr.name_raw}>{sr.name_raw}</span></span>
                      <span className="draft-row__cell num" data-label="Брутто">{b == null ? '—' : b + ' г'}</span>
                      <span className="draft-row__cell num" data-label="Нетто">{n == null ? '—' : n + ' г'}</span>
                      <span className="draft-row__cell num" data-label="Выход">{w == null ? '—' : w + ' г'}</span>
                      <span className="draft-row__cell draft-row__cell--act" />
                    </div>
                  );
                })}
              </div>
            )
            : (
              <div className="draft-rows draft-rows--sub">
                <div className="draft-rows__head">
                  <span>Компонент</span><span className="num">Брутто</span><span className="num">Нетто</span><span className="num">Выход</span><span />
                </div>
                {rows.map((sr, j) => (
                  <div className="draft-row" key={sr.key || j}>
                    <span className="draft-row__cell draft-row__cell--name" data-label="Компонент">
                      {sr.card_id != null
                        ? <span className="draft-row__bound" title={sr.name_raw}><Icon name="link" size={13} /><span className="truncate">{sr.name_raw}</span></span>
                        : <TextInput value={sr.name_raw || ''} onChange={(e) => onSubRow(rowIndex, j, { name_raw: e.target.value })} placeholder="Название" />}
                    </span>
                    <span className="draft-row__cell" data-label="Брутто"><NumberInput plain value={sr.amount_brutto_g ?? ''} unit="г" step={5} min={0} onChange={(v) => onSubRow(rowIndex, j, { amount_brutto_g: v === '' ? null : Number(v) })} /></span>
                    <span className="draft-row__cell" data-label="Нетто"><NumberInput plain value={sr.amount_netto_g ?? ''} unit="г" step={5} min={0} onChange={(v) => onSubRow(rowIndex, j, { amount_netto_g: v === '' ? null : Number(v) })} /></span>
                    <span className="draft-row__cell" data-label="Выход"><NumberInput plain value={sr.weight_in_dish_g ?? ''} unit="г" step={5} min={0} onChange={(v) => onSubRow(rowIndex, j, { weight_in_dish_g: v === '' ? null : Number(v) })} /></span>
                    <span className="draft-row__cell draft-row__cell--act"><button type="button" className="cv-iconbtn" aria-label="Удалить компонент" onClick={() => onSubRowRemove(rowIndex, j)}><Icon name="trash" size={14} /></button></span>
                  </div>
                ))}
              </div>
            )}
        {eff === 'dish'
          ? <p className="draft-subrows__empty text-mute">Превью на расход в этом блюде. Состав правится в режиме «На кг».</p>
          : <button type="button" className="draft-addrow draft-addrow--sub" onClick={() => onSubRowAdd(rowIndex)}><Icon name="plus" size={14} /> Добавить компонент</button>}
        {/* Технология п/ф: уходит в ТТК полуфабриката при «Утвердить» (draft_json.
            technology). Правка текста НЕ дёргает recompute (на числа не влияет). */}
        <label className="draft-subrows__tech"><span className="field__label">Технология п/ф</span>
          <Textarea value={sub.technology || ''} minRows={2} placeholder="Шаги приготовления п/ф — с температурой и временем…"
            onChange={(e) => onSubFieldText(rowIndex, { technology: e.target.value })} /></label>
      </div>
    );
  }

  /* ── состав ПРИВЯЗАННОГО п/ф (sub_recipe_id + sub_brief) — read-only ──
     Чат 69: строка, привязанная к уже сохранённому полуфабрикату, выглядела
     «чёрным ящиком» — состава не видно. Бэк теперь кладёт компактный состав
     в r.sub_brief; правится он НЕ здесь, а в карточке п/ф в Кухне. */
  /* Переключатель вида состава: «Для блюда» (дефолт) пересчитывает состав
     привязанного п/ф под его расход в этом блюде — factor = нетто строки-п/ф ÷
     выход п/ф; «На кг» — состав на полную массу батча, как лежит в карточке
     (прежнее поведение). Это локальный ВИД (read-only сверка) — в
     payload/recompute/commit он НЕ уходит. Если выход п/ф или расход в блюде не
     заданы, «для блюда» посчитать нельзя — показываем только батч. */
  function SubBriefRows({ brief, usageNettoG }) {
    const rows = Array.isArray(brief.rows) ? brief.rows : [];
    const outG = Number(brief.output_g);
    const useG = usageNettoG == null ? null : Number(usageNettoG);
    const canDish = Number.isFinite(outG) && outG > 0 && useG != null && Number.isFinite(useG) && useG > 0;
    const [mode, setMode] = useState('dish');
    const eff = (mode === 'dish' && canDish) ? 'dish' : 'kg';
    // «На кг» = норма на 1 кг (×1000/выход): сохранённый ПФ держит свой реальный
    // выход батча, нормируем только для показа. «Для блюда» = ×навеска/выход.
    const factor = eff === 'dish' ? useG / outG : (outG > 0 ? 1000 / outG : 1);
    const scale = (v) => (v == null ? null : Math.round(Number(v) * factor * 10) / 10);
    return (
      <div className="draft-subrows draft-subrows--readonly">
        <div className="draft-subrows__meta">
          <span className="draft-subrows__kicker">Состав привязанного полуфабриката</span>
          {canDish && (
            <Segmented options={[{ value: 'dish', label: 'Для блюда' }, { value: 'kg', label: 'На кг' }]}
              value={eff} onChange={setMode} size="sm" ariaLabel="Масштаб состава полуфабриката" />
          )}
          {eff === 'dish'
            ? <span className="text-mute">в блюде: {useG} г</span>
            : <span className="text-mute">на 1 кг</span>}
        </div>
        {rows.length === 0
          ? <p className="draft-subrows__empty text-mute">Состав пуст — заполните его в карточке полуфабриката в Кухне.</p>
          : (
            <div className="draft-rows draft-rows--sub">
              <div className="draft-rows__head">
                <span>Компонент</span><span className="num">Брутто</span><span className="num">Нетто</span><span className="num">Выход</span><span />
              </div>
              {rows.map((sr, j) => {
                const b = scale(sr.amount_brutto_g);
                const n = scale(sr.amount_netto_g);
                const w = scale(sr.weight_in_dish_g);
                return (
                  <div className="draft-row draft-row--readonly" key={j}>
                    <span className="draft-row__cell draft-row__cell--name" data-label="Компонент"><span className="truncate" title={sr.name_raw}>{sr.name_raw}</span></span>
                    <span className="draft-row__cell num" data-label="Брутто">{b == null ? '—' : b + ' г'}</span>
                    <span className="draft-row__cell num" data-label="Нетто">{n == null ? '—' : n + ' г'}</span>
                    <span className="draft-row__cell num" data-label="Выход">{w == null ? '—' : w + ' г'}</span>
                    <span className="draft-row__cell draft-row__cell--act" />
                  </div>
                );
              })}
            </div>
          )}
        <p className="draft-subrows__empty text-mute">{eff === 'dish'
          ? 'Состав пересчитан на расход полуфабриката в этом блюде. Правится он в собственной карточке в Кухне.'
          : 'Полуфабрикат уже сохранён в Кухне — его состав правится в собственной карточке, здесь он показан для сверки.'}</p>
      </div>
    );
  }

  /* ── редактор состава ТТК (row-as-card на узких через CSS) ──
     Строка либо ПРИВЯЗАНА к реестру (card_id/sub_recipe_id — имя read-only,
     КБЖУ/цена берутся из карточки; у п/ф с sub_brief — раскрытие состава
     read-only), либо РУЧНАЯ (name_raw — повар вводит имя,
     AI заведёт карточку с КБЖУ при «Утвердить и добавить»), либо П/Ф-НАБРОСОК
     (sub_create, W7) — bound-вид с раскрытием вложенного состава. */
  function TtkRows({ rows, onChange, onRemove, openSubs, onToggleSub, onSubField, onSubFieldText, onSubRow, onSubRowRemove, onSubRowAdd }) {
    return (
      <div className="draft-rows">
        <div className="draft-rows__head">
          <span>Ингредиент</span><span className="num">Брутто</span><span className="num">Нетто</span><span className="num">Выход</span><span />
        </div>
        {rows.map((r, i) => {
          // Привязка к ПФ без состава (sub_brief) и без наброска (sub_create) —
          // битая (ПФ удалён / это блюдо): показываем строку ОБЫЧНЫМ
          // ингредиентом (имя редактируемое), как «распривязать → обычный».
          // Бэк снимет фантомный sub_recipe_id на recompute/commit.
          const brokenSub = r.sub_recipe_id != null
            && !(r.sub_brief && typeof r.sub_brief === 'object')
            && !(r.sub_create && typeof r.sub_create === 'object');
          const bound = (r.card_id != null || r.sub_recipe_id != null) && !brokenSub;
          const sub = (!bound && r.sub_create && typeof r.sub_create === 'object') ? r.sub_create : null;
          const brief = (!brokenSub && r.sub_recipe_id != null && r.sub_brief && typeof r.sub_brief === 'object') ? r.sub_brief : null;
          const boundName = r.matched_sub || r.matched_card || r.target_label || r.name_raw;
          const rowKey = r.key || 'i' + i;
          const subOpen = !!((sub || brief) && openSubs && openSubs[rowKey]);
          const subCount = sub && Array.isArray(sub.ingredient_rows) ? sub.ingredient_rows.length : 0;
          const briefCount = brief && Array.isArray(brief.rows) ? brief.rows.length : 0;
          return (
            <React.Fragment key={rowKey}>
              <div className="draft-row">
                <span className="draft-row__cell draft-row__cell--name" data-label="Ингредиент">
                  {bound
                    ? brief
                      ? (
                        <button type="button" className="draft-row__bound draft-row__subtoggle" aria-expanded={subOpen} title={boundName}
                          aria-label={`${boundName} — привязанный полуфабрикат${briefCount ? `, компонентов: ${briefCount}` : ''}. ${subOpen ? 'Свернуть' : 'Раскрыть'} состав`}
                          onClick={() => onToggleSub(rowKey)}>
                          <span className="draft-row__pficons"><Icon name="box" size={13} /><Icon name="link" size={11} /></span>
                          <span className="truncate">{boundName}</span>
                          <span className="draft-row__subbadge num">привязан</span>
                          <Icon name={subOpen ? 'chevron-up' : 'chevron-down'} size={12} />
                        </button>
                      )
                      : <span className="draft-row__bound" title={boundName}><Icon name={r.sub_recipe_id != null ? 'box' : 'link'} size={13} /><span className="truncate">{boundName}</span></span>
                    : sub
                      ? (
                        <button type="button" className="draft-row__bound draft-row__subtoggle" aria-expanded={subOpen} title={sub.title || r.name_raw}
                          aria-label={`${sub.title || r.name_raw} — полуфабрикат${subCount ? `, компонентов: ${subCount}` : ''}. ${subOpen ? 'Свернуть' : 'Раскрыть'} состав`}
                          onClick={() => onToggleSub(rowKey)}>
                          <Icon name="box" size={13} />
                          <span className="truncate">{sub.title || r.name_raw}</span>
                          {/* «N комп.» = число компонентов состава (НЕ «используется в N картах») */}
                          <span className="draft-row__subbadge num">{subCount ? subCount + ' комп.' : 'п/ф'}</span>
                          <Icon name={subOpen ? 'chevron-up' : 'chevron-down'} size={12} />
                        </button>
                      )
                      : <TextInput value={r.name_raw || ''} onChange={(e) => onChange(i, { name_raw: e.target.value })} placeholder="Название" />}
                </span>
                <span className="draft-row__cell" data-label="Брутто"><NumberInput plain value={r.amount_brutto_g ?? ''} unit="г" step={5} min={0} onChange={(v) => onChange(i, { amount_brutto_g: v === '' ? null : Number(v) })} /></span>
                <span className="draft-row__cell" data-label="Нетто"><NumberInput plain value={r.amount_netto_g ?? ''} unit="г" step={5} min={0} onChange={(v) => onChange(i, { amount_netto_g: v === '' ? null : Number(v) })} /></span>
                <span className="draft-row__cell" data-label="Выход"><NumberInput plain value={r.weight_in_dish_g ?? ''} unit="г" step={5} min={0} onChange={(v) => onChange(i, { weight_in_dish_g: v === '' ? null : Number(v) })} /></span>
                <span className="draft-row__cell draft-row__cell--act"><button type="button" className="cv-iconbtn" aria-label="Удалить строку" onClick={() => onRemove(i)}><Icon name="trash" size={14} /></button></span>
              </div>
              {subOpen && sub && (
                <SubRows rowIndex={i} sub={sub} usageNettoG={r.amount_netto_g} onSubField={onSubField} onSubFieldText={onSubFieldText}
                  onSubRow={onSubRow} onSubRowRemove={onSubRowRemove} onSubRowAdd={onSubRowAdd} />
              )}
              {subOpen && brief && <SubBriefRows brief={brief} usageNettoG={r.amount_netto_g} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  /* Техпроцесс черновика → массив шагов {id,text}. Источник — payload.technology_steps
     (если уже редактировали) либо строка payload.technology (split по \n). Зеркалит
     parseTechSteps редактора Кухни — конструктор шагов вместо одного textarea. */
  function parseTechSteps(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.technology_steps)) return payload.technology_steps;
    const txt = (payload.technology || '').trim();
    if (!txt) return [];
    return txt.split(/\n+/).map((t) => t.trim()).filter(Boolean).map((t, i) => ({ id: 'st-init-' + i, text: t }));
  }

  /* Выбор способа добавления ингредиента: из реестра (сырьё/п-ф) или вручную (AI заведёт). */
  const ADD_INGREDIENT_OPTIONS = [
    { key: 'card', icon: 'tag', title: 'Ингредиент из реестра', desc: 'Закупаемое сырьё — цена и КБЖУ берутся из карточки.' },
    { key: 'semiproduct', icon: 'box', title: 'Полуфабрикат из реестра', desc: 'Готовая заготовка со своей ТТК и себестоимостью.' },
    { key: 'manual', icon: 'sparkles', title: 'Вручную — AI заведёт', desc: 'Впишите название; карточку с КБЖУ создаст AI при добавлении.' },
  ];
  function AddIngredientChoice({ onPick, onClose }) {
    return (
      <Modal open onClose={onClose} size="sm" title="Что добавить в состав?"
        footer={<Button variant="ghost" onClick={onClose}>Отмена</Button>}>
        <ul className="add-choice">
          {ADD_INGREDIENT_OPTIONS.map((o) => (
            <li key={o.key}>
              <button type="button" className="add-choice__item" onClick={() => onPick(o.key)}>
                <span className="add-choice__top">
                  <span className="add-choice__icon"><Icon name={o.icon} size={20} /></span>
                  <span className="add-choice__title">{o.title}</span>
                  <Icon name="chevron-right" size={16} className="add-choice__chev" />
                </span>
                <span className="add-choice__desc">{o.desc}</span>
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  /* ── блюдо-строка в разделе меню (row-as-card на узких, как TtkRows) ──
     Раздел вынесен в заголовок-конструктор выше; в строке — название + цена.
     Рецепта пока нет (сгенерируется позже), поэтому правим только мету. */
  function MenuDishRow({ dish, index, onChange, onRemove }) {
    return (
      <div className="draft-dish">
        <TextInput className="draft-dish__name" value={dish.name || ''} onChange={(e) => onChange(index, { name: e.target.value })} placeholder="Название блюда" />
        <NumberInput plain className="draft-dish__price" value={dish.target_price_rub ?? ''} unit="₽" step={50} min={0} onChange={(v) => onChange(index, { target_price_rub: v === '' ? null : Number(v) })} />
        <button type="button" className="cv-iconbtn draft-dish__del" aria-label="Удалить блюдо" onClick={() => onRemove(index)}><Icon name="trash" size={14} /></button>
      </div>
    );
  }

  /* Группировка блюд по разделам для конструктора. Возвращает {sections:[{name,
     items:[{d,i}]}], loose:[{d,i}]} — loose = блюда с категорией вне section_order
     (например, пустая) — показываем их отдельной нередактируемой группой. */
  function groupMenuSections(payload) {
    const dishes = payload.dishes || [];
    const order = payload.section_order || [];
    const indexed = dishes.map((d, i) => ({ d, i }));
    const norm = (s) => (s || '').trim();
    const sections = order.map((name) => ({
      name, items: indexed.filter(({ d }) => norm(d.category) === norm(name)),
    }));
    const orderSet = new Set(order.map(norm));
    const loose = indexed.filter(({ d }) => !orderSet.has(norm(d.category)));
    return { sections, loose };
  }

  // Производные числа меню из состава (код считает): кол-во, медианная цена,
  // агрегатный food cost по блюдам с заданными себестоимостью и ценой.
  function menuDerived(dishes) {
    const list = Array.isArray(dishes) ? dishes : [];
    const prices = list.map((d) => Number(d.target_price_rub)).filter((v) => v > 0).sort((a, b) => a - b);
    let median = null;
    if (prices.length) { const n = prices.length; median = n % 2 ? prices[(n - 1) / 2] : Math.round((prices[n / 2 - 1] + prices[n / 2]) / 2); }
    let tc = 0; let tp = 0;
    for (const d of list) { const c = Number(d.target_cost_rub); const p = Number(d.target_price_rub); if (c > 0 && p > 0) { tc += c; tp += p; } }
    const fc = tp > 0 ? Math.round((tc / tp) * 1000) / 10 : null;
    return { count: list.length, median, fc };
  }

  const TTK_TABS = [{ value: 'compose', label: 'Состав' }, { value: 'tech', label: 'Техпроцесс' }, { value: 'gost', label: 'Требования' }, { value: 'kbju', label: 'КБЖУ' }];

  /* ── ГОСТ-разделы черновика ТТК (вкладка «Требования») ──
     Зеркалит «Требования» редактора Кухни (TTKEditor: Р1/Р2/Р5/Р6): органолептика,
     подача, хранение + типовые разделы. Тексты живут в payload плоско (те же ключи,
     что _TTK_TEXT_KEYS commit'а) и на числа не влияют — правка БЕЗ recompute. */
  const GOST_QUALITY_FIELDS = [
    ['appearance', 'Внешний вид'], ['texture', 'Консистенция'],
    ['taste', 'Вкус'], ['smell', 'Запах'],
  ];
  const GOST_TEXT_FIELDS = [
    ['plating_and_service', 'Оформление и подача', 'Посуда, температура отпуска, срок реализации…'],
    ['storage_conditions', 'Условия и сроки хранения', 'Например: при +2..+6 °C не более 12 часов…'],
    ['application_scope', 'Область применения', 'На какое блюдо/изделие распространяется ТТК…'],
    ['raw_material_requirements', 'Требования к сырью', 'Требования к качеству и безопасности сырья…'],
    ['microbiology', 'Микробиология', 'Микробиологические показатели…'],
  ];
  function GostTab({ payload, computed, setField, toggleAllergen }) {
    // Выравнивание сетки 2×2: все четыре textarea органолептики — одной высоты,
    // по самой наполненной. Эффект родителя срабатывает ПОСЛЕ внутреннего
    // авторазмера Textarea (child-эффекты раньше parent) и перекрывает его.
    // На узких экранах (сетка в одну колонку) авторост остаётся поштучным.
    const qRefs = useRef(null);
    if (!qRefs.current) {
      qRefs.current = Object.fromEntries(GOST_QUALITY_FIELDS.map(([k]) => [k, React.createRef()]));
    }
    const qValues = GOST_QUALITY_FIELDS.map(([k]) => payload[k] || '');
    useEffect(() => {
      const nodes = GOST_QUALITY_FIELDS
        .map(([k]) => qRefs.current[k].current).filter(Boolean);
      if (nodes.length < 2) return;
      // Одна колонка (узкий вьюпорт): первая пара не на одной строке — не трогаем.
      if (nodes[0].getBoundingClientRect().top !== nodes[1].getBoundingClientRect().top) return;
      let max = 0;
      for (const n of nodes) { n.style.height = 'auto'; max = Math.max(max, n.scrollHeight); }
      for (const n of nodes) { n.style.height = max + 'px'; n.style.overflowY = 'hidden'; }
    }, qValues); // eslint-disable-line react-hooks/exhaustive-deps
    return (
      <div className="draft-gost">
        <div className="draft-gost__grid">
          {GOST_QUALITY_FIELDS.map(([key, label]) => (
            <label className="draft-field" key={key}><span className="field__label">{label}</span>
              <Textarea ref={qRefs.current[key]} value={payload[key] || ''} minRows={1} onChange={(e) => setField({ [key]: e.target.value })} /></label>
          ))}
        </div>
        {GOST_TEXT_FIELDS.map(([key, label, ph]) => (
          <label className="draft-field" key={key}><span className="field__label">{label}</span>
            <Textarea value={payload[key] || ''} minRows={2} placeholder={ph} onChange={(e) => setField({ [key]: e.target.value })} /></label>
        ))}
        <div className="draft-allergens">
          <span className="field__label">Аллергены</span>
          <div className="cv-chips">
            {ALLERGENS.map((a) => { const on = (payload.allergens || []).includes(a); return (
              <button type="button" key={a} className={'draft-chip' + (on ? ' is-on' : '')} onClick={() => toggleAllergen(a)}>{on && <Icon name="check" size={11} />}{a}</button>
            ); })}
          </div>
          {/* Авто-агрегат из состава (computed.allergens_auto, тот же расчёт, что
              в редакторе) — при утверждении объединится с ручными чипами. */}
          <AllergenAutoHint items={computed.allergens_auto || []} manual={payload.allergens || []} />
        </div>
      </div>
    );
  }

  function DraftEntityCard({ draft, messageId, chatId, onRevise, onCommitted }) {
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const { activeWorkspace } = useAuth();
    const chat = useChat ? useChat() : null;
    const supported = draft.kind === 'card' || draft.kind === 'ttk' || draft.kind === 'menu';
    const [status, setStatus] = useState(draft.status || 'editing');
    // Текст инлайн-ошибки commit'а (null → общий «Проверьте название и повторите»).
    const [commitErr, setCommitErr] = useState(null);
    const [payload, setPayload] = useState(() => {
      const p = JSON.parse(JSON.stringify(draft.payload || {}));
      // Back-compat: старые меню-черновики держали блюда в `positions`; канон —
      // `dishes` (как присылает design_menu_draft / open_menu_editor). Подхватим
      // legacy-форму, чтобы блюда не терялись при переоткрытии старого черновика.
      if (draft.kind === 'menu') {
        if (!Array.isArray(p.dishes) && Array.isArray(p.positions)) {
          p.dishes = p.positions.map((x) => ({
            name: x.name, category: x.category, short_description: x.short_description,
            portion_yield_g: x.portion_yield_g, target_cost_rub: x.target_cost_rub,
            target_price_rub: x.target_price_rub,
          }));
        }
        if (!Array.isArray(p.dishes)) p.dishes = [];
        // Порядок разделов-конструктора. Источник истины — `dishes[].category`
        // (его читает commit→_menu_create_data), а section_order лишь хранит
        // порядок и пустые разделы. Сидируем из категорий по первому появлению.
        if (!Array.isArray(p.section_order)) {
          const order = [];
          for (const d of p.dishes) { const c = (d.category || '').trim(); if (c && !order.includes(c)) order.push(c); }
          p.section_order = order;
        }
      }
      return p;
    });
    const [computed, setComputed] = useState(() => ({ ...(draft.computed || {}) }));
    const [target, setTarget] = useState(draft.committed_target || null);
    const [tab, setTab] = useState('compose');
    const [recomputing, setRecomputing] = useState(false);
    const [recomputeFailed, setRecomputeFailed] = useState(false);
    // W7 (§8.4): pre-flight предупреждения payload (output_g у п/ф-набросков).
    // Стартуют из computed кадра (agentic-раннер кладёт warnings в computed),
    // дальше обновляются ответом recompute. Непустые — блокируют «Утвердить».
    const [warnings, setWarnings] = useState(() => (
      Array.isArray(draft.computed && draft.computed.warnings) ? draft.computed.warnings : []
    ));
    // Совещательные замечания (баланс выходов, брутто<нетто) — показываем,
    // но НЕ блокируем «Утвердить» (у шефа могут быть основания).
    const [notices, setNotices] = useState(() => (
      Array.isArray(draft.computed && draft.computed.notices) ? draft.computed.notices : []
    ));
    // W7 (§20.6): раскрытие вложенного состава п/ф — ЛОКАЛЬНЫЙ UI-стейт,
    // НЕ в payload (иначе флаг утёк бы в draft_context через registerDraftLive).
    const [openSubs, setOpenSubs] = useState(() => ({}));
    const toggleSub = (key) => setOpenSubs((prev) => ({ ...prev, [key]: !prev[key] }));
    const [addOpen, setAddOpen] = useState(false);   // выбор способа добавления ингредиента
    const [picker, setPicker] = useState(null);      // {kind} — KitchenPicker открыт
    const [gramsFor, setGramsFor] = useState(null);  // entities[] ожидают ввода граммовки
    const debRef = useRef(null);
    const reqToken = useRef(0);
    const keyRef = useRef(1);   // стабильные ключи новых строк состава
    const stepRef = useRef(1);  // стабильные id шагов техпроцесса
    const forMenu = draft.for_menu || null;
    const isSemi = payload.kind === 'semiproduct'; // п/ф: без цены/food-cost, только себестоимость

    // дебаунс-пересчёт derived (НЕ триггерит скролл ленты — только локальный стейт)
    // ТИП СУЩНОСТИ для роутинга — ПЕРВЫМ аргументом (draft.kind='ttk'); payload несёт
    // ПОДТИП рецепта (kind:'dish'|'semiproduct'), его НЕ затираем — иначе бэк теряет
    // признак полуфабриката и сохраняет/считает его как блюдо (баг: ПФ уходил в «Блюда»).
    // Боевой recomputeDraft(kind, payload) → {computed, warnings}; токен против гонок ответов.
    const scheduleRecompute = useCallback((nextPayload, edited) => {
      if (draft.kind === 'card' || !chat || !chat.recomputeDraft) return; // у сырья derived не считаются
      setRecomputing(true);
      setRecomputeFailed(false);
      if (debRef.current) clearTimeout(debRef.current);
      const token = ++reqToken.current;
      debRef.current = setTimeout(() => {
        chat.recomputeDraft(draft.kind, nextPayload, edited)
          .then((res) => {
            if (token !== reqToken.current) return; // устаревший ответ — игнор
            setComputed((prev) => ({ ...prev, ...(res && res.computed ? res.computed : {}) }));
            // F41 (точное схождение выхода): бэк возвращает НОРМАЛИЗОВАННЫЙ
            // payload — правил выход → состав масштабирован; правил строку →
            // выход обновлён до суммы. Применяем ТОЛЬКО затронутые нормализацией
            // поля (output_g + ingredient_rows): полная замена затёрла бы текст
            // (технология/ГОСТ), набираемый параллельно с пересчётом — текстовые
            // правки recompute не триггерят и токеном не защищены.
            if (res && res.payload && typeof res.payload === 'object'
                && Array.isArray(res.payload.ingredient_rows)) {
              setPayload((prev) => ({
                ...prev,
                output_g: res.payload.output_g ?? prev.output_g,
                ingredient_rows: res.payload.ingredient_rows,
              }));
            }
            // W7: pre-flight предупреждения пересчитываются на каждый recompute
            // (исправил output_g → warning гаснет, кнопка разблокируется).
            if (res && Array.isArray(res.warnings)) setWarnings(res.warnings);
            if (res && Array.isArray(res.notices)) setNotices(res.notices);
            setRecomputing(false);
          })
          .catch(() => { if (token === reqToken.current) { setRecomputing(false); setRecomputeFailed(true); } });
      }, 600);
    }, [draft.kind, chat]);
    useEffect(() => () => { if (debRef.current) clearTimeout(debRef.current); }, []);

    // chat-entities-edit: публикуем живое состояние черновика наверх (провайдер) — на отправке
    // следующего сообщения снимок уходит в draft_context, и модель правит ИМЕННО этот черновик
    // с учётом ручных правок пользователя. Зеркалит frontend DraftEntityCard.registerDraftLive.
    useEffect(() => {
      if (!chat || !chat.registerDraftLive) return undefined;
      const liveStatus = status === 'committed' ? 'committed' : status === 'dismissed' ? 'dismissed' : 'draft';
      chat.registerDraftLive(draft.proposal_id, {
        proposal_id: draft.proposal_id, kind: draft.kind,
        title: payload.title || payload.name || draft.title || '',
        // payload несёт подтип рецепта (dish/semiproduct) — НЕ затираем типом сущности,
        // иначе draft_context следующего сообщения потеряет признак полуфабриката.
        payload, status: liveStatus,
      });
      return () => { if (chat.unregisterDraftLive) chat.unregisterDraftLive(draft.proposal_id); };
    }, [payload, status, draft.proposal_id, draft.kind]);

    // ── F40 W5: фоновая сборка дерева утверждённой сущности (живёт на беке) ──
    // myRoot — (kind, id) созданной сущности из commit-цели; treeJob — снимок
    // джобы из провайдера, отфильтрованный по ЭТОМУ корню (один диалог собирает
    // одно дерево за раз). Деревья растут и у меню, и у ТТК-бандлов.
    const myRoot = (draft.kind === 'menu' || draft.kind === 'ttk') && target && target.id != null
      ? { kind: draft.kind, id: target.id } : null;
    const rawJob = chat && chat.treeFillFor ? chat.treeFillFor(chatId) : null;
    const treeJob = (rawJob && myRoot != null
      && rawJob.rootKind === myRoot.kind && rawJob.rootId === myRoot.id) ? rawJob : null;

    // Завершение сборки: один тост + очистка записи у провайдера (чат перестаёт
    // считаться «занятым», кнопка «Стоп» композера сменяется на отправку).
    // Недостроенные узлы → локальный снимок для кнопки «Достроить» (запись у
    // провайдера к этому моменту уже очищена).
    // Commit, пропустивший сборку дерева закрытым доступом, помечает черновик
    // (metadata tree_build_skipped) — карточка поднимает «Достроить» (новый
    // прогон по корню) и причину и после перемонтирования.
    const skippedOnMount = draft.tree_build_skipped === SUBSCRIPTION_EXPIRED;
    const [failedTree, setFailedTree] = useState(() => (skippedOnMount ? { mode: 'restart', failed: 0 } : null));
    // Пространство без активного тарифа: сущность создана, а дерево не собрано
    // (commit пропустил сборку) или остановлено (бэк закрыл доступ к модели на
    // ходу). Текст причины + «Выбрать тариф» под свёрнутой карточкой.
    const [aiNote, setAiNote] = useState(() => treeSkippedNote(draft));
    // Флаг в metadata выставлен (при mount или этим commit) — снять после успешной достройки.
    const skippedFlagRef = useRef(skippedOnMount);
    // Остановлено доступом: недостроенные узлы ждут в waiting (+ упавшие до
    // остановки) — после оплаты resume этой же джобы их продолжит.
    const holdStoppedTree = (job, note) => {
      setAiNote(note);
      const left = (job.waiting || 0) + (job.failed || 0)
        || Math.max(0, (job.total || 0) - (job.done || 0));
      setFailedTree({ mode: 'resume', failed: left, jobId: job.jobId });
    };

    // Восстановление после refresh: committed-сущность при mount проверяет, нет
    // ли на беке активной джобы сборки (её мог запустить прошлый сеанс). Если
    // есть — провайдер поднимет индикатор и polling. resumeTreeFill сам не дублирует.
    // Остановленную закрытым доступом джобу провайдер возвращает снимком (в общий
    // слот диалога она не пишется — он один на чат, а корней в чате бывает
    // несколько): причину и «Достроить» карточка держит у себя, без тоста.
    useEffect(() => {
      if (status !== 'committed' || myRoot == null) return undefined;
      if (!chat || !chat.resumeTreeFill) return undefined;
      let alive = true;
      Promise.resolve(chat.resumeTreeFill(chatId, myRoot.kind, myRoot.id)).then((snap) => {
        const note = alive ? treeStopNote(snap) : null;
        if (note) holdStoppedTree(snap, note);
      }).catch(() => {});
      return () => { alive = false; };
    }, [status, myRoot && myRoot.id, chatId]);

    const jobStatus = treeJob && treeJob.status;
    const jobActive = !!(treeJob && treeJob.active);
    useEffect(() => {
      if (!treeJob || treeJob.active || !chat || !chat.clearTreeFill) return;
      const stopNote = treeJob.status === 'cancelled' ? treeStopNote(treeJob) : null;
      if (stopNote) {
        toast.show(stopNote, 'warning', 8000);
        holdStoppedTree(treeJob, stopNote);
      } else if (treeJob.status === 'cancelled') toast.show('Сборка рецептов остановлена', 'muted');
      else if (treeJob.failed) {
        const names = (treeJob.failedTitles || []).slice(0, 3).join(', ');
        toast.show(`Готово, кроме ${treeJob.failed}${names ? ` (${names})` : ''} — можно достроить из карточки`, 'warning');
        setFailedTree({ mode: 'resume', failed: treeJob.failed, jobId: treeJob.jobId });
      } else if (treeJob.truncated) {
        // Потолок одной сборки: часть узлов не создана (не failed!) — достройка
        // = НОВЫЙ прогон по корню (стабы пересеются), не resume этой джобы.
        toast.show(`Готово, но ${treeJob.truncated} поз. не вошли в лимит одной сборки — можно достроить из карточки`, 'warning');
        setFailedTree({ mode: 'restart', failed: treeJob.truncated });
      } else toast.show('Рецепты и составы сгенерированы', 'success');
      // адресно: слот диалога мог уже занять другой корень — его не трогаем
      chat.clearTreeFill(chatId, myRoot.kind, myRoot.id);
    }, [jobStatus, jobActive]);

    const setField = (patch, recompute, edited) => {
      setPayload((prev) => { const next = { ...prev, ...patch }; if (recompute) scheduleRecompute(next, edited); return next; });
    };
    const setRow = (i, patch) => setPayload((prev) => {
      const rows = (prev.ingredient_rows || []).slice(); rows[i] = { ...rows[i], ...patch };
      const next = { ...prev, ingredient_rows: rows }; scheduleRecompute(next, 'rows'); return next;
    });
    const removeRow = (i) => setPayload((prev) => { const rows = (prev.ingredient_rows || []).slice(); rows.splice(i, 1); const next = { ...prev, ingredient_rows: rows }; scheduleRecompute(next, 'rows'); return next; });
    const toggleAllergen = (a) => setPayload((p) => { const cur = p.allergens || []; return { ...p, allergens: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] }; });

    // ── W7 (§20.6): ГЛУБОКИЙ иммутабельный патч вложенного состава п/ф ──
    // sub_create.ingredient_rows правится копированием всей цепочки
    // rows[i] → sub_create → ingredient_rows[j] (shallow-spread терял бы правку).
    const patchSub = (i, fn) => setPayload((prev) => {
      const rows = (prev.ingredient_rows || []).slice();
      const sc = { ...(rows[i] && rows[i].sub_create ? rows[i].sub_create : {}) };
      fn(sc);
      rows[i] = { ...rows[i], sub_create: sc };
      const next = { ...prev, ingredient_rows: rows }; scheduleRecompute(next, 'rows'); return next;
    });
    const setSubField = (i, patch) => patchSub(i, (sc) => Object.assign(sc, patch));
    // Текстовые поля п/ф (технология): без recompute — на числа не влияют.
    const setSubFieldText = (i, patch) => setPayload((prev) => {
      const rows = (prev.ingredient_rows || []).slice();
      rows[i] = { ...rows[i], sub_create: { ...(rows[i] && rows[i].sub_create ? rows[i].sub_create : {}), ...patch } };
      return { ...prev, ingredient_rows: rows };
    });
    const setSubRow = (i, j, patch) => patchSub(i, (sc) => {
      const srs = (sc.ingredient_rows || []).slice(); srs[j] = { ...srs[j], ...patch };
      sc.ingredient_rows = srs;
    });
    const removeSubRow = (i, j) => patchSub(i, (sc) => {
      const srs = (sc.ingredient_rows || []).slice(); srs.splice(j, 1);
      sc.ingredient_rows = srs;
    });
    const addSubRow = (i) => patchSub(i, (sc) => {
      sc.ingredient_rows = [...(sc.ingredient_rows || []), {
        name_raw: '', amount_brutto_g: null, amount_netto_g: null,
        weight_in_dish_g: null, key: 'sk' + (keyRef.current++),
      }];
    });

    // ── Меню-конструктор: разделы-заголовки + блюда внутри (аналог состава ТТК) ──
    // Источник истины — dishes[].category (его читает commit); section_order хранит
    // порядок разделов и пустые разделы. Правки идут по индексу блюда / раздела.
    const setDish = (i, patch) => setPayload((prev) => { const ds = (prev.dishes || []).slice(); ds[i] = { ...ds[i], ...patch }; return { ...prev, dishes: ds }; });
    const removeDish = (i) => setPayload((prev) => { const ds = (prev.dishes || []).slice(); ds.splice(i, 1); return { ...prev, dishes: ds }; });
    const addDishToSection = (sectionName) => setPayload((prev) => ({ ...prev, dishes: [...(prev.dishes || []), { name: '', category: sectionName, target_price_rub: null, key: 'd' + (keyRef.current++) }] }));
    const addSection = () => setPayload((prev) => {
      const order = (prev.section_order || []).slice();
      let name = 'Новый раздел'; let n = 2; while (order.includes(name)) { name = 'Новый раздел ' + (n++); }
      return { ...prev, section_order: [...order, name] };
    });
    const renameSection = (idx, newName) => setPayload((prev) => {
      const order = (prev.section_order || []).slice(); const old = order[idx]; if (old === undefined) return prev;
      order[idx] = newName;
      const ds = (prev.dishes || []).map((d) => ((d.category || '').trim() === (old || '').trim() ? { ...d, category: newName } : d));
      return { ...prev, section_order: order, dishes: ds };
    });
    const removeSection = (idx) => setPayload((prev) => {
      const order = (prev.section_order || []).slice(); const name = order.splice(idx, 1)[0];
      const ds = (prev.dishes || []).filter((d) => (d.category || '').trim() !== (name || '').trim());
      return { ...prev, section_order: order, dishes: ds };
    });
    const moveSection = (idx, dir) => setPayload((prev) => {
      const order = (prev.section_order || []).slice(); const j = idx + dir; if (j < 0 || j >= order.length) return prev;
      [order[idx], order[j]] = [order[j], order[idx]]; return { ...prev, section_order: order };
    });

    // ── Добавление ингредиента: ручной ввод (AI заведёт карточку) или из реестра ──
    const addManualRow = () => setPayload((p) => ({ ...p, ingredient_rows: [...(p.ingredient_rows || []), { name_raw: '', amount_brutto_g: null, amount_netto_g: null, weight_in_dish_g: null, key: 'k' + (keyRef.current++) }] }));
    // MultiGramsStep → строки черновика: card → card_id, п/ф (target_kind='ttk') → sub_recipe_id.
    const addPickedRows = (picked) => {
      const mapped = (picked || []).map((r) => {
        const row = { name_raw: r.target_label || r.name_raw, amount_brutto_g: r.amount_brutto_g, amount_netto_g: r.amount_netto_g, weight_in_dish_g: r.weight_in_dish_g, unit: r.unit || 'г', key: 'k' + (keyRef.current++) };
        if (r.target_kind === 'ttk') { row.sub_recipe_id = r.target_id; row.matched_sub = r.target_label; }
        else { row.card_id = r.target_id; row.matched_card = r.target_label; }
        return row;
      });
      setPayload((p) => { const next = { ...p, ingredient_rows: [...(p.ingredient_rows || []), ...mapped] }; scheduleRecompute(next); return next; });
      setGramsFor(null); setPicker(null);
    };
    const onAddPick = (key) => {
      setAddOpen(false);
      if (key === 'manual') addManualRow();
      else setPicker({ kind: key }); // 'card' | 'semiproduct'
    };

    // ── Техпроцесс: конструктор шагов (зеркалит редактор Кухни) ──
    const techSteps = parseTechSteps(payload);
    const setTechSteps = (next) => setField({ technology_steps: next, technology: next.map((s) => s.text).join('\n') });
    const addStep = () => setTechSteps([...techSteps, { id: 'st' + (stepRef.current++), text: '' }]);
    const updateStep = (sid, text) => setTechSteps(techSteps.map((s) => (s.id === sid ? { ...s, text } : s)));
    const moveStep = (sid, dir) => { const arr = [...techSteps]; const i = arr.findIndex((s) => s.id === sid); const j = i + dir; if (j < 0 || j >= arr.length) return; const t = arr[i]; arr[i] = arr[j]; arr[j] = t; setTechSteps(arr); };
    const deleteStep = (sid) => setTechSteps(techSteps.filter((s) => s.id !== sid));

    // Меню: каждое блюдо = 1 запрос квоты. Перед материализацией предупреждаем
    // (всегда, пока не выбран «Больше не спрашивать»). Безлимит → без модалки.
    // Возвращает true (продолжить commit) / false (отмена).
    const confirmMenuQuota = async () => {
      const n = (payload.dishes || []).length;
      if (n <= 0) return true;
      let used = null;
      let limit = null;
      try {
        const sub = await subscriptionReal.getSubscription();
        used = sub && sub.usage ? sub.usage.chat_messages_used : null;
        limit = sub && sub.limits ? sub.limits.chat_messages_per_30d : null;
      } catch { /* деградируем: покажем только число блюд */ }
      if (limit === -1) return true; // безлимит — стоимости нет, не предупреждаем
      if (localStorage.getItem(MENU_QUOTA_SKIP_KEY) === '1') return true;
      const remaining = (limit != null && used != null) ? Math.max(0, limit - used) : null;
      const body = remaining != null
        ? `В меню ${n} ${plBlyud(n)}. Сборка спишет до ${n} запросов — по одному за каждое собранное блюдо, по мере готовности (осталось ${remaining} из ${limit}). Если квота закончится в процессе, соберутся только блюда, которые поместятся.`
        : `В меню ${n} ${plBlyud(n)}. Сборка спишет до ${n} запросов — по одному за каждое собранное блюдо.`;
      const res = await confirm({
        title: 'Сборка меню спишет запросы',
        body,
        confirmLabel: 'Создать',
        cancelLabel: 'Отмена',
        checkboxLabel: 'Больше не спрашивать',
      });
      if (!res) return false;
      if (res.checked) {
        try { localStorage.setItem(MENU_QUOTA_SKIP_KEY, '1'); } catch { /* noop */ }
      }
      return true;
    };

    const commit = async () => {
      if (!chat || !chat.commitDraft) return;
      // W7 (§8.4): pre-flight блок — без output_g у п/ф commit упадёт 400.
      if (warnings.length > 0) { toast.show(warnings[0], 'warning'); return; }
      // F40 W4: не коммитим, пока держатель не сохранён (см. committable) —
      // иначе message_id=null обходит бэк-гарды дубля линии.
      if (numericMsgId(messageId) == null) { toast.show('Дождитесь завершения ответа', 'muted'); return; }
      // Меню: предупреждаем о списании запросов по числу блюд (всегда, пока не
      // выбрано «Больше не спрашивать»). Отмена в модалке — не коммитим.
      if (draft.kind === 'menu') {
        const ok = await confirmMenuQuota();
        if (!ok) return;
      }
      setStatus('committing');
      // Тип сущности ('ttk'/'card'/'menu') — отдельным аргументом kind; payload несёт
      // ПОДТИП рецепта (dish/semiproduct), его НЕ затираем (иначе ПФ создаётся как блюдо).
      // Боевой commit → {id, url, kind, editor_route, bound_recipes, tree_build_job?,
      // tree_build_skipped?}; дубль → throw err.error==='duplicate'
      // (code: 'duplicate_title' у ТТК / 'duplicate_name' у карточки). chatId — чтобы
      // джоба привязалась к диалогу (кнопка «Стоп»).
      chat.commitDraft({ messageId: numericMsgId(messageId), proposalId: draft.proposal_id, kind: draft.kind, payload, chatId })
        .then((res) => {
          const tgt = { kind: res.kind || draft.kind, id: res.id, url: res.url || res.editor_route };
          setTarget(tgt);
          // зеркалим committed в metadata (переоткрытие чата покажет «Создано»)
          if (chat.replaceDraft) chat.replaceDraft(draft.proposal_id, { status: 'committed', committed_target: tgt });
          // F35 п.14: рецепты блюд, привязанные при коммите ЭТОГО меню → «Создано» со ссылкой на ТТК
          for (const bp of (res.bound_recipes || [])) {
            if (chat.replaceDraft) chat.replaceDraft(bp.proposal_id, { status: 'committed', committed_target: { kind: 'ttk', id: bp.ttk_id, url: `/kitchen?tab=recipes&edit=ttk-${bp.ttk_id}` } });
          }
          setStatus('committed');
          onCommitted && onCommitted(draft.proposal_id, tgt);
          // Живой счётчик: коммит меню списал по 1 запросу на блюдо — сигналим
          // переключателю пространства перезапросить расход (без перезагрузки).
          try { window.dispatchEvent(new CustomEvent('quota:changed')); } catch { /* noop */ }
          // сигнал игровому онбордингу: первая сущность реально создана
          try {
            window.dispatchEvent(new CustomEvent('onb:committed', { detail: {
              kind: draft.kind,
              id: tgt.id,   // id созданной сущности — для удаления демо-данных в конце онбординга
              title: payload.title || payload.name || draft.title || '',
              costText: computed.cost_per_portion_rub != null ? fmt.money(computed.cost_per_portion_rub) : null,
              fcText: computed.food_cost_pct != null ? fmt.percent(computed.food_cost_pct, { digits: 1 }) : null,
            } }));
          } catch { /* noop */ }
          // F40 W5: рецепты/составы утверждённой сущности достраивает СЕРВЕРНАЯ
          // джоба дерева (TreeBuildJob, меню→блюда→п/ф→сырьё) — живёт на беке и
          // переживает refresh. Поднимаем прогресс через провайдер (он поллит
          // статус + держит чат «занятым» для кнопки «Стоп»); тост о завершении
          // даст эффект сборки, не commit.
          const job = res.tree_build_job;
          if (job && job.total > 0 && chat.startTreeFill) {
            chat.startTreeFill(chatId, job);
          } else if (res.tree_build_skipped === 'quota_exceeded') {
            // W6: квота-гейт не дал запустить сборку — сущность создана, но
            // рецепты НЕ сгенерируются. Молчать нельзя (раньше тут был
            // success-тост при пустых стабах).
            toast.show('Лимит запросов тарифа исчерпан — блюда не собраны. Повысьте тариф или попробуйте позже.', 'warning');
          } else if (treeSkippedNote(res)) {
            // Доступ к модели закрыт (пространство без тарифа): сущность создана,
            // рецепты НЕ собирались. Зелёное «Добавлено в систему» тут — ложь.
            // «Достроить» = новый прогон по корню: пока доступ закрыт, бэк ответит
            // 402 и кнопка останется; после оплаты — соберёт. Флаг кладём в
            // metadata черновика, чтобы кнопка и причина пережили перемонтирование.
            const note = treeSkippedNote(res);
            toast.show(note, 'warning', 8000);
            setAiNote(note);
            setFailedTree({ mode: 'restart', failed: 0 });
            skippedFlagRef.current = true;
            if (chat.replaceDraft) chat.replaceDraft(draft.proposal_id, { tree_build_skipped: SUBSCRIPTION_EXPIRED });
          } else {
            toast.show('Добавлено в систему', 'success');
          }
        })
        .catch((err) => {
          if (err && (err.error === 'duplicate' || err.code === 'duplicate_title' || err.code === 'duplicate_name')) {
            // Дубль названия: инлайн-ошибка с конкретикой вместо общего текста.
            setCommitErr(err.code === 'duplicate_name'
              ? 'Не удалось сохранить. Карточка с таким названием уже существует.'
              : 'Не удалось сохранить. ТТК с таким названием уже существует.');
            setStatus('error');
            // Игровой онбординг обязателен (без пропуска) — на дубле нельзя застрять:
            // сущность с таким именем уже есть, поэтому продвигаем тур дальше.
            try { window.dispatchEvent(new CustomEvent('onb:committed', { detail: { kind: draft.kind, exists: true } })); } catch { /* noop */ }
          }
          else if (err && err.code === 'line_committed') {
            // F40 W4 commit-guard: другая версия этой линии уже утверждена —
            // схлопываем галерею на неё, дубль не создаём (план §18.4).
            const tgt = { kind: err.committed_kind || draft.kind, id: err.committed_id };
            setTarget(tgt);
            if (chat.replaceDraft) chat.replaceDraft(draft.proposal_id, { status: 'committed', committed_target: tgt });
            setStatus('committed');
            toast.show(`Уже утверждена версия ${err.committed_version_n || ''}`.trim(), 'muted');
          }
          else { setCommitErr(null); setStatus('error'); }
        });
    };
    const dismiss = () => { setStatus('dismissed'); if (chat && chat.replaceDraft) chat.replaceDraft(draft.proposal_id, { status: 'dismissed' }); };
    const revise = () => { onRevise && onRevise(`Уточнить черновик «${payload.title || payload.name || draft.title}»: `); };

    // URL открытия созданной сущности: из target.url или деривим по kind+id (history-кейс).
    const targetUrl = (t) => {
      if (!t) return null;
      if (t.url) return t.url;
      const tab = t.kind === 'card' ? 'ingredients' : t.kind === 'menu' ? 'menus' : 'recipes';
      return `/kitchen?tab=${tab}&edit=${t.kind}-${t.id}`;
    };
    const undismiss = () => { setStatus('editing'); if (chat && chat.replaceDraft) chat.replaceDraft(draft.proposal_id, { status: 'draft' }); };

    /* ── collapsed states ── */
    // F40 W5 (§8.7): пока на беке активна джоба сборки дерева ЭТОГО корня —
    // показываем прогресс (done/total по узлам). Переживает refresh: после
    // перезагрузки committed-карточка переоткрывает джобу через resumeTreeFill.
    // Остановить — кнопкой «Стоп» композера (чат «занят», см. ChatProvider).
    if (treeJob && treeJob.active) {
      return (
        <div className="ai-card ai-card--collapsed">
          <Icon name="spinner" size={15} className="spin" />
          <span>Собираю рецепты и составы: <span className="num">{treeJob.done}/{treeJob.total}</span></span>
        </div>
      );
    }
    if (status === 'committed') {
      const url = targetUrl(target);
      return (
        <>
        <div className="ai-card ai-card--collapsed ai-card--ok">
          <Icon name="check-circle" size={15} />
          <span>Добавлено: <span className="cv-strong">{payload.title || payload.name || draft.title}</span></span>
          {failedTree && chat && chat.resumeFailedTree && (
            // Джоба закончилась с недостроенными узлами → «Достроить».
            // mode 'resume' — failed-узлы вернутся в работу; mode 'restart' —
            // узлы срезаны потолком (их НЕТ) → новый прогон по корню.
            <button
              type="button" className="ai-card__relink"
              onClick={() => {
                const started = failedTree.mode === 'restart' && chat.restartTreeBuild && myRoot
                  ? chat.restartTreeBuild(chatId, myRoot.kind, myRoot.id)
                  : chat.resumeFailedTree(chatId, failedTree.jobId);
                // Только true = сборка запущена. Любой отказ (закрытый доступ,
                // квота, 500, сеть) провайдер отдаёт false и сам тостит причину:
                // кнопку и пометку оставляем — повторить можно.
                Promise.resolve(started).then((ok) => {
                  if (ok !== true) return;
                  setFailedTree(null);
                  setAiNote(null);
                  if (skippedFlagRef.current && chat.replaceDraft) {
                    skippedFlagRef.current = false;
                    chat.replaceDraft(draft.proposal_id, { tree_build_skipped: null });
                  }
                }).catch(() => {});
              }}
            >
              Достроить{failedTree.failed > 0 ? ` (${failedTree.failed})` : ''} <Icon name="refresh" size={12} />
            </button>
          )}
          {url && <button type="button" className="ai-card__relink" onClick={() => navigate(url)}>Открыть в редакторе <Icon name="arrow-up-right" size={12} /></button>}
        </div>
        {aiNote && (
          <div className="callout callout--warn" role="note" style={{ marginTop: 6 }}>
            <Icon name="alert-triangle" size={15} />
            <div className="grow">
              {aiNote}{' '}
              <button type="button" className="ai-card__relink" style={{ marginLeft: 0 }}
                      onClick={() => navigate(tariffTarget(activeWorkspace))}>
                Выбрать тариф <Icon name="arrow-up-right" size={12} />
              </button>
            </div>
          </div>
        )}
        </>
      );
    }
    if (status === 'dismissed') {
      return <div className="ai-card ai-card--collapsed"><Icon name="x-circle" size={15} /><span>Черновик «{payload.title || payload.name || draft.title}» отклонён</span><button type="button" className="ai-card__relink" onClick={undismiss}>Вернуть</button></div>;
    }

    /* ── fallback: неподдержанный тип (ВСЕГДА с кнопкой закрытия — фикс №22) ── */
    if (!supported) {
      return (
        <div className="ai-card ai-card--fallback">
          <div className="ai-card__head"><span className="ai-card__mark"><Icon name="sparkles" size={13} /></span><span className="ai-card__kicker">AI-черновик</span></div>
          <div className="ai-card__title">Черновик {KIND_LABEL[draft.kind] ? '«' + KIND_LABEL[draft.kind] + '»' : 'неизвестного типа'} пока не поддержан</div>
          <p className="ai-card__fallback-note text-mute">Этот тип черновика нельзя отредактировать в ленте. Вы можете закрыть карточку.</p>
          <div className="ai-card__foot"><button type="button" className="btn btn--secondary btn--sm" onClick={dismiss}><Icon name="close" size={14} /><span className="btn__label">Закрыть</span></button></div>
        </div>
      );
    }

    const busy = status === 'committing';
    // F40 W4: коммит доступен только когда сообщение-держатель УЖЕ сохранено
    // (серверный 'm…'-id). Пока ход стримится (локальный 'a…'-id → numericMsgId
    // null) бэк-гарды _existing_commit/_line_already_committed по message_id не
    // работают → дубль линии проскочил бы. Кнопка разблокируется на onDone.
    const committable = numericMsgId(messageId) != null;
    const title = payload.title || payload.name || draft.title || 'Черновик';

    return (
      <div className={'ai-card ai-card--draft ai-card--' + draft.kind} data-onb="draft-card">
        <div className="ai-card__head">
          <span className="ai-card__mark" aria-hidden="true"><Icon name={KIND_ICON[draft.kind]} size={13} /></span>
          <span className="ai-card__kicker">AI предлагает · {KIND_LABEL[draft.kind]}{draft.version_n > 1 ? ' · версия ' + draft.version_n : ''}</span>
          <span className="ai-card__state"><Icon name="clock" size={13} />к утверждению</span>
        </div>

        <input className="ai-card__title-input" value={title}
          onChange={(e) => setField(draft.kind === 'card' ? { name: e.target.value } : { title: e.target.value })}
          aria-label="Название черновика" />

        {forMenu && (
          <div className="ai-card__bound"><Icon name="link" size={13} /> Привязан к меню: <span className="cv-strong">{forMenu.menu_title}</span></div>
        )}

        {/* ── ТЕЛО по типу ── */}
        {draft.kind === 'card' && (
          <div className="draft-fields">
            <label className="draft-field"><span className="field__label">Категория</span>
              <NativeSelect value={payload.category || 'other'} options={CATEGORIES} onChange={(e) => setField({ category: e.target.value })} /></label>
            <label className="draft-field"><span className="field__label">Ед. учёта</span>
              {/* Канон 'г'/'шт' (= IngredientUnit), как присылает LLM/бэк — раньше тут были 'g'/'pcs',
                  поэтому выбор LLM никогда не подсвечивался (обе пилюли серые). Переключение на 'г'
                  сбрасывает массу штуки (зеркалит IngredientEditor.setUnit). */}
              <Segmented options={[{ value: 'г', label: 'Граммы' }, { value: 'шт', label: 'Штуки' }]} value={payload.default_unit || 'г'} onChange={(v) => setField(v === 'шт' ? { default_unit: 'шт' } : { default_unit: 'г', piece_weight_g: null })} size="sm" /></label>
            {(payload.default_unit || 'г') === 'шт' && (
              <label className="draft-field"><span className="field__label">Граммов в 1 штуке</span>
                <NumberInput plain value={payload.piece_weight_g ?? ''} unit="г" step={1} min={0} onChange={(v) => setField({ piece_weight_g: v === '' ? null : Number(v) })} />
                <span className="field__msg field__hint">Масса 1 шт — для пересчёта в граммы.</span></label>
            )}
            <div className="draft-kbju">
              <span className="field__label">КБЖУ на 100 г</span>
              <div className="draft-kbju__grid">
                <NumberInput plain value={payload.kcal_per_100g ?? ''} unit="ккал" step={1} min={0} onChange={(v) => setField({ kcal_per_100g: v === '' ? null : Number(v) })} />
                <NumberInput plain value={payload.protein_per_100g ?? ''} unit="Б" step={0.1} min={0} onChange={(v) => setField({ protein_per_100g: v === '' ? null : Number(v) })} />
                <NumberInput plain value={payload.fat_per_100g ?? ''} unit="Ж" step={0.1} min={0} onChange={(v) => setField({ fat_per_100g: v === '' ? null : Number(v) })} />
                <NumberInput plain value={payload.carbs_per_100g ?? ''} unit="У" step={0.1} min={0} onChange={(v) => setField({ carbs_per_100g: v === '' ? null : Number(v) })} />
              </div>
            </div>
            <div className="draft-allergens">
              <span className="field__label">Аллергены</span>
              <div className="cv-chips">
                {ALLERGENS.map((a) => { const on = (payload.allergens || []).includes(a); return (
                  <button type="button" key={a} className={'draft-chip' + (on ? ' is-on' : '')} onClick={() => toggleAllergen(a)}>{on && <Icon name="check" size={11} />}{a}</button>
                ); })}
              </div>
            </div>
          </div>
        )}

        {draft.kind === 'ttk' && (
          <div className="draft-ttk">
            <div className="draft-ttk__meta">
              <label className="draft-field draft-field--sm"><span className="field__label">Тип</span>
                <Segmented options={[{ value: 'dish', label: 'Блюдо' }, { value: 'semiproduct', label: 'П/ф' }]} value={payload.kind || 'dish'} onChange={(v) => setField({ kind: v }, true)} size="sm" /></label>
              <label className="draft-field draft-field--sm"><span className="field__label">Выход</span>
                <NumberInput plain value={payload.output_g ?? ''} unit="г" step={10} min={0} onChange={(v) => setField({ output_g: v === '' ? null : Number(v) }, true, 'output')} /></label>
              {/* П/ф не продаётся гостю — цены и food-cost у него нет, только себестоимость. */}
              {!isSemi && (
                <label className="draft-field draft-field--sm"><span className="field__label">Цена</span>
                  <NumberInput plain value={payload.selling_price_rub ?? ''} unit="₽" step={10} min={0} onChange={(v) => setField({ selling_price_rub: v === '' ? null : Number(v) }, true)} /></label>
              )}
            </div>
            <Segmented as="tab" options={TTK_TABS} value={tab} onChange={setTab} size="sm" className="draft-ttk__tabs" ariaLabel="Разделы черновика" />
            {tab === 'compose' && (
              <div className="draft-compose">
                <TtkRows rows={payload.ingredient_rows || []} onChange={setRow} onRemove={removeRow}
                  openSubs={openSubs} onToggleSub={toggleSub} onSubField={setSubField} onSubFieldText={setSubFieldText}
                  onSubRow={setSubRow} onSubRowRemove={removeSubRow} onSubRowAdd={addSubRow} />
                <button type="button" className="draft-addrow" onClick={() => setAddOpen(true)}><Icon name="plus" size={14} /> Добавить ингредиент</button>
              </div>
            )}
            {tab === 'tech' && (
              <div className="draft-steps">
                {techSteps.length === 0
                  ? <p className="draft-steps__empty text-mute">Опишите приготовление по шагам — так процесс проще читать на кухне.</p>
                  : (
                    <ol className="draft-steps__list">
                      {techSteps.map((s, i) => (
                        <li key={s.id} className="draft-step">
                          <span className="draft-step__num num">{i + 1}</span>
                          <Textarea value={s.text} minRows={1} placeholder={'Шаг ' + (i + 1) + '…'} className="draft-step__text" onChange={(e) => updateStep(s.id, e.target.value)} />
                          <span className="draft-step__act">
                            <button type="button" className="cv-iconbtn" aria-label="Выше" disabled={i === 0} onClick={() => moveStep(s.id, -1)}><Icon name="chevron-up" size={13} /></button>
                            <button type="button" className="cv-iconbtn" aria-label="Ниже" disabled={i === techSteps.length - 1} onClick={() => moveStep(s.id, 1)}><Icon name="chevron-down" size={13} /></button>
                            <button type="button" className="cv-iconbtn" aria-label="Удалить шаг" onClick={() => deleteStep(s.id)}><Icon name="trash" size={13} /></button>
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                <button type="button" className="draft-addrow" onClick={addStep}><Icon name="plus" size={14} /> Добавить шаг</button>
              </div>
            )}
            {tab === 'gost' && (
              <GostTab payload={payload} computed={computed} setField={setField} toggleAllergen={toggleAllergen} />
            )}
            {tab === 'kbju' && (
              <KbjuTable per_portion={computed.kbju_per_portion} per_100g={computed.kbju_per_100g} className="derived-zone draft-kbju-table" />
            )}
            <div className="draft-computed derived-zone">
              <span className="draft-computed__item">Себест.: <b className="num">{computed.cost_per_portion_rub != null ? fmt.money(computed.cost_per_portion_rub) : '—'}</b></span>
              {!isSemi && <span className="draft-computed__item">FC: <b className="num">{computed.food_cost_pct != null ? fmt.percent(computed.food_cost_pct, { digits: 1 }) : '—'}</b></span>}
              {recomputing && <span className="draft-computed__busy"><Icon name="spinner" size={12} className="spin" /> Пересчёт…</span>}
              {!recomputing && recomputeFailed && (
                <button type="button" className="draft-computed__retry" onClick={() => scheduleRecompute(payload)}>
                  <Icon name="alert-circle" size={12} /> Не удалось пересчитать — повторить
                </button>
              )}
            </div>

            {/* Выбор способа добавления ингредиента + пикеры реестра (issue: вручную vs из списка) */}
            {addOpen && <AddIngredientChoice onPick={onAddPick} onClose={() => setAddOpen(false)} />}
            {picker && (
              <KitchenPicker open kind={picker.kind} multi
                fetcher={(s) => pickerFetch(picker.kind, s)}
                onClose={() => setPicker(null)}
                onConfirm={(entities) => { setPicker(null); if (entities && entities.length) setGramsFor(entities); }} />
            )}
            {gramsFor && (
              <MultiGramsStep open entities={gramsFor}
                onBack={() => { setPicker({ kind: gramsFor[0] && gramsFor[0].kind === 'ttk' ? 'semiproduct' : 'card' }); setGramsFor(null); }}
                onClose={() => setGramsFor(null)}
                onConfirm={addPickedRows} />
            )}
          </div>
        )}

        {draft.kind === 'menu' && (() => {
          const dishes = payload.dishes || [];
          const md = menuDerived(dishes);
          const { sections, loose } = groupMenuSections(payload);
          return (
            <div className="draft-fields">
              <label className="draft-field"><span className="field__label">Концепция</span>
                <Textarea value={payload.concept || ''} onChange={(e) => setField({ concept: e.target.value })} rows={2} placeholder="Концепция меню…" /></label>
              <div className="cv-edit-row">
                <label className="draft-field draft-field--sm"><span className="field__label">Средний чек</span>
                  <NumberInput plain value={payload.average_check ?? ''} unit="₽" step={50} min={0} onChange={(v) => setField({ average_check: v === '' ? null : Number(v) })} /></label>
                <label className="draft-field draft-field--sm"><span className="field__label">Целевой FC%</span>
                  <NumberInput plain value={payload.target_food_cost_pct ?? ''} unit="%" step={1} min={0} onChange={(v) => setField({ target_food_cost_pct: v === '' ? null : Number(v) })} /></label>
              </div>
              <div className="draft-menu">
                <span className="field__label">Блюда{md.count ? ' · ' + md.count : ''}</span>
                {sections.length === 0 && loose.length === 0 && (
                  <p className="draft-steps__empty text-mute">Пока нет разделов — добавьте раздел и блюда будущего меню. Рецепт каждого блюда сгенерируется позже.</p>
                )}
                {sections.map((sec, si) => (
                  <section className="draft-section" key={'sec-' + si}>
                    <div className="draft-section__head">
                      <TextInput value={sec.name} className="draft-section__name" placeholder="Название раздела"
                        onChange={(e) => renameSection(si, e.target.value)} aria-label="Название раздела" />
                      <span className="draft-section__count num">{sec.items.length}</span>
                      <button type="button" className="cv-iconbtn" aria-label="Раздел выше" disabled={si === 0} onClick={() => moveSection(si, -1)}><Icon name="chevron-up" size={13} /></button>
                      <button type="button" className="cv-iconbtn" aria-label="Раздел ниже" disabled={si === sections.length - 1} onClick={() => moveSection(si, 1)}><Icon name="chevron-down" size={13} /></button>
                      <button type="button" className="cv-iconbtn" aria-label="Удалить раздел" onClick={() => removeSection(si)}><Icon name="trash" size={13} /></button>
                    </div>
                    <div className="draft-section__dishes">
                      {sec.items.map(({ d, i }) => <MenuDishRow key={d.key || i} dish={d} index={i} onChange={setDish} onRemove={removeDish} />)}
                      <button type="button" className="draft-addrow" onClick={() => addDishToSection(sec.name)}><Icon name="plus" size={14} /> Добавить блюдо</button>
                    </div>
                  </section>
                ))}
                {loose.length > 0 && (
                  <section className="draft-section draft-section--loose">
                    <div className="draft-section__head"><span className="draft-section__name text-mute">Без раздела</span><span className="draft-section__count num">{loose.length}</span></div>
                    <div className="draft-section__dishes">
                      {loose.map(({ d, i }) => <MenuDishRow key={d.key || i} dish={d} index={i} onChange={setDish} onRemove={removeDish} />)}
                    </div>
                  </section>
                )}
                <button type="button" className="draft-addrow draft-addsection" onClick={addSection}><Icon name="plus" size={14} /> Добавить раздел</button>
              </div>
              {dishes.length > 0 && (md.median != null || md.fc != null) && (
                <div className="draft-computed derived-zone">
                  {md.median != null && <span className="draft-computed__item">Медианная цена: <b className="num">{fmt.money(md.median)}</b></span>}
                  {md.fc != null && <span className="draft-computed__item">Средний FC: <b className="num">{fmt.percent(md.fc, { digits: 1 })}</b></span>}
                </div>
              )}
            </div>
          );
        })()}

        {/* W7 (§8.4): pre-flight предупреждения payload — ДО клика «Утвердить» */}
        {draft.kind === 'ttk' && warnings.length > 0 && (
          <div className="ai-card__err ai-card__warnings">
            <Icon name="alert-circle" size={14} />
            <div className="ai-card__warnings-list">{warnings.map((w, i) => <div key={i}>{w}</div>)}</div>
          </div>
        )}
        {/* Совещательные замечания: показываем, кнопку НЕ блокируем */}
        {draft.kind === 'ttk' && notices.length > 0 && (
          <div className="ai-card__err ai-card__warnings ai-card__warnings--soft">
            <Icon name="info" size={14} />
            <div className="ai-card__warnings-list">{notices.map((w, i) => <div key={i}>{w}</div>)}</div>
          </div>
        )}
        {status === 'error' && <div className="ai-card__err"><Icon name="alert-circle" size={14} /> {commitErr || 'Не удалось сохранить. Проверьте название и повторите.'}</div>}

        <div className="ai-card__foot">
          {!forMenu && <button type="button" data-onb="draft-commit" className="btn btn--primary btn--sm" onClick={commit} disabled={busy || !committable || warnings.length > 0} title={!committable ? 'Дождитесь завершения ответа' : (warnings.length ? 'Исправьте предупреждения выше' : undefined)}>{busy && <Icon name="spinner" size={14} className="spin" />}<span className="btn__label">{status === 'error' ? 'Повторить' : 'Утвердить и добавить'}</span></button>}
          {forMenu && <span className="ai-card__bound-note text-mute"><Icon name="info" size={13} /> Будет создан вместе с меню</span>}
          <button type="button" className="btn btn--ghost btn--sm" onClick={revise} disabled={busy}><Icon name="edit" size={14} /><span className="btn__label">Уточнить</span></button>
          <button type="button" className="ai-card__link" onClick={dismiss} disabled={busy}>Отклонить</button>
        </div>
      </div>
    );
  }

  /* ── F40 W4: галерея версий одной линии черновика (план §7/§7.1) ──
     Правка-в-чате = новая версия; старые висят. Группа версий (одной
     version_group_id, собранная across-messages) сворачивается в ОДНУ карточку
     с табами «Версия 1/2/…». Дефолт — закоммиченная (если есть) либо последняя
     активная. Внутри — обычный DraftEntityCard выбранной версии (его messageId
     = сообщение-держатель этой версии, для идемпотентного commit). */
  function DraftVersionGroup({ versions, chatId, onRevise, onCommitted }) {
    const sorted = versions; // уже отсортированы по version_n (MessageList)
    const pickDefault = useCallback((list) => {
      const committed = list.findIndex((v) => v.status === 'committed');
      if (committed >= 0) return committed;
      for (let i = list.length - 1; i >= 0; i--) if (!list[i].superseded) return i;
      return list.length - 1;
    }, []);
    const [sel, setSel] = useState(() => pickDefault(sorted));
    const prevLen = useRef(sorted.length);
    const userPicked = useRef(false);
    // Появилась новая версия (линия выросла) → прыгаем на неё, НО только если
    // пользователь не выбрал вкладку вручную (иначе утащили бы его с правок старой
    // версии — её карточка размонтируется и потеряет несохранённый ввод).
    useEffect(() => {
      if (sorted.length > prevLen.current && !userPicked.current) setSel(pickDefault(sorted));
      prevLen.current = sorted.length;
    }, [sorted.length, pickDefault, sorted]);
    const pick = (i) => { userPicked.current = true; setSel(i); };
    const idx = Math.min(sel, sorted.length - 1);
    const cur = sorted[idx];
    return (
      <div className="draft-versions">
        <div className="draft-versions__tabs" role="tablist" aria-label="Версии черновика">
          {sorted.map((v, i) => (
            <button type="button" key={v.proposal_id} role="tab" aria-selected={i === idx}
              className={'draft-versions__tab' + (i === idx ? ' is-active' : '')
                + (v.status === 'committed' ? ' is-committed' : '')
                + (v.superseded && v.status !== 'committed' ? ' is-old' : '')}
              onClick={() => pick(i)}>
              Версия {v.version_n}
              {v.status === 'committed' && <Icon name="check" size={11} />}
            </button>
          ))}
        </div>
        <DraftEntityCard key={cur.proposal_id} draft={cur} messageId={cur._msgId}
          chatId={chatId} onRevise={onRevise} onCommitted={onCommitted} />
      </div>
    );
  }

export { DraftEntityCard, DraftVersionGroup };
